import { type SendingRequestBundle, SendingsDashboardType } from '@domain/types';
import { DraftDocumentsActions, refreshDraftCounters } from '@store/documents/outgoing/draft.slice';
import type { QueryClient } from '@tanstack/react-query';
import {
  findSenderIdentityInCache,
  invalidateDashboardCache,
  updateDocumentInDashboardCache,
  updateDocumentInDetailCache,
} from '@utils/caching.util';
import {
  professionalToIdentityReference,
  senderV2ToIdentityReference,
} from '@utils/identities.util';
import type { useDispatch } from 'react-redux';

import { invalidateSendingRequestsV2Count } from './invalidateSendingRequestsV2Count.util';
import {
  applySendingRequestsV2CountDelta,
  findSenderIdentityV2InCache,
  invalidateSendingRequestsV2Queries,
  type SendingRequestsV2Scope,
} from './sendingRequestsV2Cache.util';

/**
 * Everything a caller would otherwise have to know before writing to a sending request's caches:
 * which dashboard cache is actually rendered. Bound once, so the events stay narrow — and so the
 * whole thing empties out when v1 goes.
 */
export type SendingRequestCacheContext = {
  useV2SendingDashboards: boolean;
};

/** Whichever shape the call site happens to hold: v1 keys the sender on `id`, v2 on `resourceId`. */
export type SendingRequestSender = { type: string } & ({ id: string } | { resourceId: string });

export type UserChange = {
  /** The document whose row and detail changed. */
  documentId: string;
  /**
   * The sender, when the mutation's own response carries one. Left out otherwise — never
   * substituted with the identity the user happens to have selected, see `resolveSenderIdentity`.
   */
  sender?: SendingRequestSender | null;
  /**
   * The document to read the sender off when `documentId` is too new to be cached — a duplicate,
   * a grouped document. Defaults to `documentId`.
   */
  inheritsSenderFrom?: string;
  /** Required: every write moves the count by something, or admits it cannot say. */
  count: UserChangeCount;
  /**
   * `'document'` (default): scope to the resolved sender's own bucket, on `dashboardType`.
   *
   * `'sharedEntity'`: the write denormalizes onto every sender sharing the entity — a patient
   * rename, a trusted contact — so every identity bucket on both Draft and Sent is swept instead
   * of just the resolved sender's. See *Shared-entity write* in CONTEXT.md.
   *
   * `'all'`: skip attribution entirely and sweep every v2 query. For a write whose own document
   * moves between identity buckets (a sender change) — `UserChange` names one identity, and this
   * write needs two.
   */
  scope?: 'document' | 'sharedEntity' | 'all';
  /** Which dashboard's cache to scope a `'document'` write to. Defaults to Draft. */
  dashboardType?: SendingsDashboardType.Draft | SendingsDashboardType.Sent;
};

/**
 * `{ delta }` patches the cached total, so the badge moves without a request — `delta: 0` still
 * matters, it re-checks the filtered buckets (see `applySendingRequestsV2CountDelta`).
 *
 * `'serverDecides'` is for a write whose arithmetic only the server knows — a 1→N split — and
 * invalidates instead. Spelled out rather than left to an absent field: the two are a real choice,
 * and a forgotten `delta` used to look exactly like a deliberate one.
 */
export type UserChangeCount = { delta: number } | 'serverDecides';

/**
 * The caches holding a sending request, behind the events that move it.
 *
 * `remote*` is the server echo — pusher, or the poller standing in for it. It never carries a count
 * delta: the server decided what moved, and the same event shape covers a document update and a
 * patient rename, so the count can only be re-read. `userChange` is the second family: it knows its
 * delta and must not wait on the debounce.
 */
export type SendingRequestCache = {
  /** The server signalled a change on this document. The payload is not in hand yet. */
  remoteChange(documentId: string): void;
  /** The payload is in hand. Follows a `remoteChange` for the same document. */
  remoteUpdate(bundle: SendingRequestBundle): void;

  /**
   * A user action that changed one of this sender's own Draft documents — a link, a field, a
   * status. Immediate, unlike `remoteChange`: the user is looking at the result now, not 30s from
   * now.
   *
   * Callers hand over what they hold and nothing else; attribution is resolved here so the dozen
   * sites cannot disagree about it — that disagreement was the whole of DOCS-4763, and three
   * shipped bugs after it.
   *
   * Returns synchronously. The v2 count moves before this returns, and `counterAction` is known
   * before any request goes out, so neither badge waits on the network.
   */
  userChange(change: UserChange): UserChangeResult;
};

/**
 * `counterAction` is the Draft total's move, already attributed — the caller dispatches it, or
 * `yield put`s it from a saga, and decides nothing. It is returned rather than dispatched because
 * two call sites are sagas: `put` has to be yielded to stay traceable, and reaching for the store
 * singleton would pull the root saga into this module's graph.
 *
 * Absent only when the client knows nothing moved (`delta: 0`). Present with `identity: undefined`
 * when attribution failed, which moves the aggregate total and leaves every per-identity bucket
 * alone: broad, never wrong. A `count` only the server works out (`'serverDecides'`, a split) has no
 * delta to patch with, so this forces a resync instead — see `draftCounterActionFor`.
 *
 * `settled` covers the list and detail invalidations, for the one caller that keeps a spinner up
 * until fresh rows land.
 */
export type UserChangeResult = {
  counterAction?: DraftCounterAction;
  settled: Promise<void>;
};

type DraftCounterAction =
  | ReturnType<typeof DraftDocumentsActions.incrementDraftCounter>
  | ReturnType<typeof DraftDocumentsActions.decrementDraftCounter>
  | ReturnType<typeof refreshDraftCounters>;

/**
 * The Draft total moves by the same delta the v2 count does, credited to the same identity — that
 * is the whole point of resolving it here. Two action creators rather than one signed action
 * because both still have emitters outside this module; collapsing them is for when they don't.
 *
 * `'serverDecides'` (a split: one document becomes N, by a count only the server knows) cannot be
 * patched onto either bucket, so this asks for a forced resync instead of moving nothing — the same
 * asymmetry `userChange` already has on the v2 side, which invalidates its count rather than
 * patching it for the very same case.
 */
function draftCounterActionFor(
  count: UserChangeCount,
  identity: string | undefined,
): DraftCounterAction | undefined {
  if (count === 'serverDecides') return refreshDraftCounters({ force: true });
  if (count.delta === 0) return undefined;

  return count.delta < 0
    ? DraftDocumentsActions.decrementDraftCounter({ identity, count: -count.delta })
    : DraftDocumentsActions.incrementDraftCounter({ identity, count: count.delta });
}

/**
 * Two sources, in this order, and deliberately no third:
 *
 * 1. the sender the server just answered with;
 * 2. the sender on the document's last cached dashboard row.
 *
 * Which cache holds that row is the flag's business, not the caller's: the flag already decides
 * which dashboard renders, so it decides which cache is populated. Reading the other one would be
 * reading a cache nobody keeps fresh — at most a single startup prefetch, under v2.
 *
 * The identity the user currently has selected is **not** a fallback. It is a guess about someone
 * else's document, and a wrong guess invalidates one bucket while leaving the right one stale for
 * the 24h `staleTime`. `undefined` here means "genuinely unknown" and costs a wide sweep on the v2
 * side and an unattributed total on the Redux side — both broad, both never wrong.
 */
function resolveSenderIdentity(
  queryClient: QueryClient,
  { documentId, sender, inheritsSenderFrom }: UserChange,
  useV2SendingDashboards: boolean,
): string | undefined {
  if (sender) {
    return 'resourceId' in sender
      ? senderV2ToIdentityReference(sender)
      : professionalToIdentityReference(sender);
  }

  const cachedOn = inheritsSenderFrom ?? documentId;

  return useV2SendingDashboards
    ? findSenderIdentityV2InCache(queryClient, cachedOn)
    : findSenderIdentityInCache(queryClient, cachedOn);
}

/**
 * The v1 Draft dashboard, under the flag off — the mirror of what `remoteUpdate` already does for
 * the v1 row. Every caller used to have to remember `invalidateDraftDashboardForDocument` on top of
 * `userChange`; nothing in the type said so, and eleven of them forgot at once.
 *
 * Scoped by the identity `userChange` already resolved, so it attributes wherever the raw `sender`
 * the old call site passed was absent. Draft only: the three Sent-scoped writes keep their own
 * `refetchType: 'active'` invalidation, which is stronger than this rather than a duplicate of it.
 */
function invalidateV1DraftDashboard(
  queryClient: QueryClient,
  senderIdentity: string | undefined,
): void {
  invalidateDashboardCache({
    dashboardType: SendingsDashboardType.Draft,
    identity: undefined,
    refetchType: 'none',
    queryClient,
  });

  if (senderIdentity) {
    invalidateDashboardCache({
      dashboardType: SendingsDashboardType.Draft,
      identity: senderIdentity,
      refetchType: 'none',
      queryClient,
    });
  }
}

/**
 * `identities: 'any'` on both dashboards, because a realtime event carries a resource id and
 * nothing else: a patient rename — denormalized onto every sender's row — is indistinguishable
 * here from a plain document update. Sweeping wide is cheap, since invalidateQueries only marks
 * stale and only the active query refetches.
 *
 * One shared object, never rebuilt: invalidateSendingRequestsV2Count dedupes pending scopes in a
 * Set keyed by reference, so a fresh literal per event would turn a 10-event burst into 10 count
 * invalidations instead of the one it exists to collapse them into.
 */
const REMOTE_SCOPE: SendingRequestsV2Scope = {
  dashboardType: [SendingsDashboardType.Draft, SendingsDashboardType.Sent],
  identities: 'any',
};

export function createSendingRequestCache(
  queryClient: QueryClient,
  { useV2SendingDashboards }: SendingRequestCacheContext,
): SendingRequestCache {
  return {
    remoteChange(documentId) {
      if (!useV2SendingDashboards) return;

      invalidateSendingRequestsV2Queries(queryClient, {
        ...REMOTE_SCOPE,
        documentIds: [documentId],
        keyScopes: ['list', 'detail'],
      });
      invalidateSendingRequestsV2Count(queryClient, REMOTE_SCOPE);
    },

    remoteUpdate(bundle) {
      // Read by legacyDocument on screens that have no v2 twin yet, whichever dashboard renders.
      updateDocumentInDetailCache({ bundle, queryClient });

      // v2 rows were invalidated by the remoteChange that preceded this, and there is no adaptor
      // from a v1 bundle to a v2 row to patch them with anyway.
      if (useV2SendingDashboards) return;

      updateDocumentInDashboardCache({
        updatedDocument: bundle.document,
        updatedDocumentEntities: bundle.entities,
        queryClient,
      });
    },

    userChange(change) {
      if (change.scope === 'all') {
        // No identity to attribute: the document itself is moving between buckets (a sender
        // change), so the Draft total still moves unattributed, same as an unresolved sender
        // below — broad, never wrong.
        const counterAction = draftCounterActionFor(change.count, undefined);
        if (!useV2SendingDashboards) {
          // No identity to scope by, so only the workspace bucket. The two callers on this scope
          // sweep wider on their own v1 side anyway.
          invalidateV1DraftDashboard(queryClient, undefined);
          return { counterAction, settled: Promise.resolve() };
        }

        return {
          counterAction,
          settled: invalidateSendingRequestsV2Queries(queryClient, { all: true }).then(
            () => undefined,
          ),
        };
      }

      const senderIdentity = resolveSenderIdentity(queryClient, change, useV2SendingDashboards);

      if (change.scope === 'sharedEntity') {
        const counterAction = draftCounterActionFor(change.count, senderIdentity);
        if (!useV2SendingDashboards) {
          invalidateV1DraftDashboard(queryClient, senderIdentity);
          return { counterAction, settled: Promise.resolve() };
        }

        const dashboardType = [SendingsDashboardType.Draft, SendingsDashboardType.Sent];
        const sharedScope: SendingRequestsV2Scope = {
          dashboardType,
          identities: 'any',
          documentIds: [change.documentId],
          keyScopes: ['list', 'detail'],
        };
        const settled = invalidateSendingRequestsV2Queries(queryClient, sharedScope);
        // Not awaited, same reasoning as the delta patch below — and a filtered count bucket
        // still needs invalidating even though delta:0 moves no total.
        applySendingRequestsV2CountDelta(queryClient, {
          dashboardType,
          identities: 'any',
          delta: 0,
        });

        return { counterAction, settled: settled.then(() => undefined) };
      }

      // 'document' (default): the Draft total is not the v2 count and does not answer to this
      // flag — it is the same sidebar under either dashboard — but it has no Sent pendant, so it
      // only moves when this write is actually scoped to Draft.
      const dashboardType = change.dashboardType ?? SendingsDashboardType.Draft;
      const counterAction =
        dashboardType === SendingsDashboardType.Draft
          ? draftCounterActionFor(change.count, senderIdentity)
          : undefined;

      if (!useV2SendingDashboards) {
        if (dashboardType === SendingsDashboardType.Draft) {
          invalidateV1DraftDashboard(queryClient, senderIdentity);
        }
        return { counterAction, settled: Promise.resolve() };
      }

      if (!senderIdentity) {
        return {
          counterAction,
          settled: invalidateSendingRequestsV2Queries(queryClient, { all: true }).then(
            () => undefined,
          ),
        };
      }

      const scope: SendingRequestsV2Scope = {
        dashboardType,
        identities: [senderIdentity],
        documentIds: [change.documentId],
        keyScopes: ['list', 'detail'],
      };
      const settled = invalidateSendingRequestsV2Queries(queryClient, scope);

      // Not awaited: the badge moves now, not once the rows come back.
      if (change.count === 'serverDecides') {
        invalidateSendingRequestsV2Queries(queryClient, { ...scope, keyScopes: ['count'] });
      } else {
        applySendingRequestsV2CountDelta(queryClient, {
          dashboardType,
          identities: [senderIdentity],
          delta: change.count.delta,
        });
      }

      return { counterAction, settled: settled.then(() => undefined) };
    },
  };
}

/**
 * `userChange` hands back the counter action rather than dispatching it — two callers are sagas,
 * where `put` must be yielded to stay traceable. Every plain React caller immediately does the same
 * two-line dance (call it, dispatch if present); this is that dance, named once.
 */
export function applyUserChange(
  sendingRequestCache: SendingRequestCache,
  dispatch: ReturnType<typeof useDispatch>,
  change: UserChange,
): UserChangeResult {
  const result = sendingRequestCache.userChange(change);
  if (result.counterAction) dispatch(result.counterAction);
  return result;
}
